/**
 * Money + currency contracts — marketplace-wide.
 *
 * Locked 2026-06-23 by `.tondocs/payments/ADR_CURRENCY_PIN.md` (currency-pin
 * axis pivoted from country to `profiles.currency`). Shapes ride the boundary
 * (response bodies, eligibility predicates, currency-change UX) so they live
 * in `@kraahl/contract` rather than in `lib/`.
 *
 * `Iso4217` mirrors `lib/shipping/native-currency.ts` (47 countries) — the
 * canonical TS map. The shape stays minimal; `Money` is `{ amount, currency }`
 * with no formatting concerns (`lib/currency/format.ts` handles display).
 *
 * `CarrierEligibility` is the result of the per-listing predicate
 * `isCarrierAvailable(listing, carrierId)` consumed by:
 *  - Listing create/edit UI (disable `shipping_mode='live_rate'` toggle)
 *  - Server validation on listing create/edit (reject 400 on mismatch)
 *  - `resolve-checkout-intent` (defensive fallback for legacy mismatches)
 *
 * `ChangeProfileCurrencyResult` + `Blocker` are the STUB return shapes for
 * `lib/profile/currency-change.ts` (MVP returns `NOT_IMPLEMENTED`). Locked
 * now so future UX work doesn't churn callers. F-11 (2026-06-24) widened
 * the `Blocker` union from 5 → 9 variants to cover the four in-flight states
 * a future preview UX must surface (open cart / offer / auction-bid /
 * in-flight label); `IN_FLIGHT_REFUND` is deliberately NOT a blocker per the
 * refund-in-capture-currency policy (ADR §"Consequences").
 *
 * @see .tondocs/payments/ADR_CURRENCY_PIN.md §"Type / schema contracts locked"
 * @see lib/shipping/native-currency.ts — TS mirror of native_currency() SQL
 * @see lib/shipping/carrier-eligibility.ts — predicate consumer
 * @see lib/profile/currency-change.ts — MVP stub
 */

/**
 * Tuple-of-codes constant used by Zod `z.enum(...)` consumers (e.g.
 * `CreateListingResponseSchema.currency`). The {@link Iso4217} union is
 * derived from this tuple so a new code added here automatically widens the
 * type — the array↔union pin below is the drift gate.
 *
 * Mirrors `NATIVE_CURRENCY` keys in `lib/shipping/native-currency.ts`
 * (pinned by `tests/unit/fitness-native-currency-sync.test.ts`).
 */
export const ISO_4217_CODES = [
  "USD", "EUR", "GBP", "CAD", "AUD", "NZD", "JPY", "TWD",
  "CHF", "SEK", "NOK", "DKK", "PLN", "CZK", "HUF",
  "HKD", "SGD", "KRW", "THB", "MYR", "IDR", "PHP", "VND",
  "INR", "AED", "ILS", "ZAR",
  "MXN", "BRL", "ARS", "CLP", "COP",
  "TRY",
] as const;

export type Iso4217 = (typeof ISO_4217_CODES)[number];

// Compile-time exhaustiveness pin — same pattern as REFUND_STATUSES /
// CARRIER_PROVIDERS. Tautological at the source (the union is derived from
// the tuple) but catches downstream `as Iso4217` casts that widen the type.
type _AssertIso4217KindsCoverUnion =
  (typeof ISO_4217_CODES)[number] extends Iso4217 ? true : never;
type _AssertIso4217NoExtraKinds =
  Iso4217 extends (typeof ISO_4217_CODES)[number] ? true : never;

const _pinIso4217KindsCoverUnion: _AssertIso4217KindsCoverUnion = true;
const _pinIso4217NoExtraKinds: _AssertIso4217NoExtraKinds = true;
void _pinIso4217KindsCoverUnion;
void _pinIso4217NoExtraKinds;

export interface Money {
  amount: number;
  currency: Iso4217;
}

/**
 * @deprecated Use {@link CarrierProvider} from `./carrier`. `CarrierId` is
 * an alias kept for the `CarrierEligibility` shape — new code should import
 * `CarrierProvider` directly. F-03 unified the 8 previous parallel
 * declarations to a single `CARRIER_PROVIDERS` const tuple.
 */
import type { CarrierProvider } from "./carrier";
export type CarrierId = CarrierProvider;

export type CarrierEligibilityCode =
  /** `listing.currency` ≠ `native_currency(listing.ship_from_country)` — gate
   *  blocks live-rate (Buffon-class sellers stay on legacy flat-rate). */
  | "CURRENCY_MISMATCH"
  /** Listing's resolved ship-from country is not in the carrier's accepted
   *  origin set (MVP: Shippo accepts US only). */
  | "UNSUPPORTED_ORIGIN_COUNTRY"
  /** Carrier id not present in `CARRIER_REGISTRY` — programming error. */
  | "CARRIER_NOT_CONFIGURED";

export type CarrierEligibility =
  | { ok: true; carrier: CarrierId }
  | { ok: false; reason: CarrierEligibilityCode };

/**
 * Discriminator tuple — exhaustive list of every {@link Blocker} `kind`. Used
 * by the array↔union pin below (same pattern as `REFUND_STATUSES` ⇄
 * `RefundStatus` in `./refund-status.ts`) to catch additions to one side
 * without the other.
 *
 * F-11 (2026-06-24, devil's advocate audit) widened the union from 5 → 9
 * variants. Pre-F-11 the contract carried only:
 *   NOT_IMPLEMENTED | ACTIVE_AUCTION | PENDING_ORDER
 *   | WALLET_MISMATCH | RATE_LIMIT_EXCEEDED
 * That set silently missed four legitimate in-flight states a future
 * preview UX must surface:
 *   - `OPEN_CART`        — buyer's cart still holds items priced in the old
 *                          currency (would refresh to new currency under FX).
 *   - `OPEN_OFFER`       — outstanding negotiation thread carries an offer
 *                          amount in the old currency.
 *   - `OPEN_AUCTION_BID` — buyer has standing bids on auctions (the seller-
 *                          side `ACTIVE_AUCTION` is the inverse case).
 *   - `IN_FLIGHT_LABEL`  — a label has been purchased but not yet shipped /
 *                          delivered — `purchased.labelCost.vendorActual`
 *                          would now disagree with the new wallet currency
 *                          for refund/recovery math.
 *
 * NOT added on policy grounds: `IN_FLIGHT_REFUND`. Refunds are denominated in
 * the *capture* currency (`seller_orders.currency` at the moment of capture),
 * not in the seller's *current* profile currency — see memory
 * `project_refund_in_capture_currency.md` and
 * `.tondocs/payments/ADR_CURRENCY_PIN.md §"Consequences"`. A profile-currency
 * flip therefore does NOT invalidate an in-flight refund.
 */
export const BLOCKER_KINDS = [
  /** MVP stub — `lib/profile/currency-change.ts` returns this until UX lands. */
  "NOT_IMPLEMENTED",
  /** Seller-side: an auction this profile owns is live (would re-denominate the
   *  reserve / current bid). */
  "ACTIVE_AUCTION",
  /** A captured order is mid-fulfilment (`seller_orders.status='pending'` or
   *  `'shipped'`); refunds will still credit in capture currency, but the
   *  active-state UX flag surfaces "you have money in motion right now". */
  "PENDING_ORDER",
  /** Seller's connected wallet (PayPal / Stripe) is on a currency that
   *  disagrees with the new profile currency — payouts would FX-bleed. */
  "WALLET_MISMATCH",
  /** Throttle to deter accidental rapid-fire flips that would each trigger
   *  re-FX of listing pricing. */
  "RATE_LIMIT_EXCEEDED",
  /** Buyer-side: cart still holds items denominated in the old currency. F-11. */
  "OPEN_CART",
  /** Buyer-side: an outstanding negotiation thread (offer extended or
   *  received) carries an old-currency amount. F-11. */
  "OPEN_OFFER",
  /** Buyer-side: standing bid on an open auction. F-11. */
  "OPEN_AUCTION_BID",
  /** A `seller_orders` row has `label_status='pending'`/`'purchasing'`/
   *  `'purchased'` — the label-cost SoT-divergence pair (PLAN §O.1.6) would
   *  drift the buyer-paid vs vendor-billed currency axes. F-11. */
  "IN_FLIGHT_LABEL",
] as const;

/**
 * Reason a `changeCurrency()` call cannot proceed. Each variant carries the
 * identifying payload an operator / UX preview needs to surface "what's in
 * the way" without a second round-trip.
 */
export type Blocker =
  | { kind: "NOT_IMPLEMENTED" }
  | { kind: "ACTIVE_AUCTION"; auctionId: string }
  | { kind: "PENDING_ORDER"; orderId: string }
  | { kind: "WALLET_MISMATCH"; provider: "paypal" | "stripe" }
  | { kind: "RATE_LIMIT_EXCEEDED"; nextAllowedAt: string }
  | { kind: "OPEN_CART"; itemCount: number }
  | { kind: "OPEN_OFFER"; offerId: string }
  | { kind: "OPEN_AUCTION_BID"; auctionId: string }
  | { kind: "IN_FLIGHT_LABEL"; sellerOrderId: string };

// Compile-time exhaustiveness pin — array↔union, same shape as
// `_AssertIso4217KindsCoverUnion` above and `_AssertKindsCoverUnion` in
// `refund-status.ts`. Drifting one side without the other (e.g. extending the
// union with a 10th variant but forgetting to add it to `BLOCKER_KINDS`, or
// vice versa) fails the build here before any downstream code is touched.
type _AssertBlockerKindsCoverUnion =
  (typeof BLOCKER_KINDS)[number] extends Blocker["kind"] ? true : never;
type _AssertBlockerNoExtraKinds =
  Blocker["kind"] extends (typeof BLOCKER_KINDS)[number] ? true : never;

const _pinBlockerKindsCoverUnion: _AssertBlockerKindsCoverUnion = true;
const _pinBlockerNoExtraKinds: _AssertBlockerNoExtraKinds = true;
void _pinBlockerKindsCoverUnion;
void _pinBlockerNoExtraKinds;

export type ChangeProfileCurrencyResult =
  | { ok: true; affectedListings: number; fxRateApplied: number }
  | { ok: false; blockers: Blocker[] };
